/**
 * The pieces every page is built from: the frame, links that route in place,
 * a party as the graph knows it, a grant list with provenance on every row,
 * and the loading and empty states.
 *
 * `PartyLink` is where the listing rule reaches the screen (invariant 12): a
 * listed organization is a link to its page, an unlisted EIN is a name with
 * its number and no link, a name alone is the name the filer wrote, and a
 * sponsored project is shown under its sponsor. `GrantRow` is where invariant
 * 8 reaches the screen: the source, the filing or page or award, and the
 * period stand beside every amount. Nothing here shows a street address or a
 * person; the items carry neither.
 */
import type { ReactNode } from 'react';
import type { GrantEdge, Party, Provenance } from '../../src/serve/items.js';
import { formatAmount, formatCount, formatEin, formatPeriod, identifierTail, sourceLabel } from './format.js';
import { onLinkClick } from './router.js';

export const Link = ({ to, children, className }: { to: string; children: ReactNode; className?: string }) => (
  <a href={to} onClick={onLinkClick(to)} className={className ?? 'text-teal-800 underline decoration-teal-300 underline-offset-2 hover:decoration-teal-700'}>
    {children}
  </a>
);

export const Frame = ({ children }: { children: ReactNode }) => (
  <div className="min-h-screen flex flex-col">
    <header className="border-b border-stone-200 bg-white">
      <div className="mx-auto max-w-5xl px-4 py-3 flex items-baseline gap-6">
        <Link to="/" className="text-lg font-semibold tracking-tight text-stone-900 no-underline">
          FunderGraph
        </Link>
        <span className="text-sm text-stone-500">Who funds what, from public filings, with the source on every fact.</span>
      </div>
    </header>
    <main className="mx-auto w-full max-w-5xl px-4 py-6 flex-1">{children}</main>
    <footer className="border-t border-stone-200 text-xs text-stone-500">
      <div className="mx-auto max-w-5xl px-4 py-4">
        Facts come from IRS filings, funder websites, and federal award records, each cited where it appears. Identifiers under{' '}
        <code>https://fundergraph.org/id/</code> name things, not pages. No individual is named anywhere in the graph.
      </div>
    </footer>
  </div>
);

export const Section = ({ title, children, aside }: { title: string; children: ReactNode; aside?: ReactNode }) => (
  <section className="mt-8">
    <div className="flex items-baseline justify-between gap-4 border-b border-stone-200 pb-1">
      <h2 className="text-base font-semibold text-stone-800">{title}</h2>
      {aside === undefined ? null : <div className="text-sm text-stone-500">{aside}</div>}
    </div>
    <div className="mt-3">{children}</div>
  </section>
);

export const Loading = ({ what }: { what: string }) => <p className="text-stone-500">Loading {what}…</p>;

export const Empty = ({ children }: { children: ReactNode }) => <p className="text-stone-500">{children}</p>;

export const Problem = ({ error }: { error: Error }) => (
  <p role="alert" className="rounded border border-red-200 bg-red-50 p-3 text-red-800">
    {error.message}
  </p>
);

export const Badge = ({ children, tone = 'stone' }: { children: ReactNode; tone?: 'stone' | 'teal' | 'amber' }) => {
  const tones = { stone: 'bg-stone-100 text-stone-700', teal: 'bg-teal-50 text-teal-800', amber: 'bg-amber-50 text-amber-800' } as const;
  return <span className={`inline-block rounded px-1.5 py-0.5 text-xs ${tones[tone]}`}>{children}</span>;
};

export const PartyLink = ({ party, individual = false }: { party: Party; individual?: boolean }) => {
  if (individual) {
    return <span className="text-stone-500">An individual (name withheld)</span>;
  }
  if (party.ein !== null && party.listed) {
    return (
      <>
        <Link to={`/org/${party.ein}`}>{party.name ?? formatEin(party.ein)}</Link> <span className="text-stone-500 text-xs">{formatEin(party.ein)}</span>
      </>
    );
  }
  if (party.ein !== null) {
    return (
      <>
        {party.name ?? 'Name not stated'} <span className="text-stone-500 text-xs">{formatEin(party.ein)} · no page: no return on record</span>
      </>
    );
  }
  if (party.id !== null) {
    return <span>{party.name ?? identifierTail(party.id)}</span>;
  }
  return (
    <>
      {party.name ?? 'Name not stated'}
      {party.project === null ? <span className="text-stone-500 text-xs"> · as the funder wrote it</span> : <span className="text-stone-500 text-xs"> · a project of the sponsor</span>}
    </>
  );
};

export const ProvenanceNote = ({ provenance }: { provenance: Provenance }) => (
  <span className="text-xs text-stone-500">
    {sourceLabel(provenance.source)}
    {provenance.objectId !== null && provenance.source === 'irs-990' ? ` · return ${provenance.objectId}` : ''}
    {provenance.page !== null ? (
      <>
        {' · '}
        <a href={provenance.page} className="underline" rel="noreferrer">
          page
        </a>
        {provenance.capturedAt !== null ? ` captured ${provenance.capturedAt.slice(0, 10)}` : ''}
      </>
    ) : null}
    {provenance.source === 'usaspending' ? ` · award record` : ''}
    {` · ${formatPeriod(provenance.period)}`}
  </span>
);

const TIER_LABEL: Readonly<Record<string, string>> = { exact: 'exact name match', rule: 'name and place rule', vector: 'nearest name, corroborated' };

export const GrantRow = ({ edge, side }: { edge: GrantEdge; side: 'funder' | 'recipient' }) => {
  const party = side === 'funder' ? edge.funder : edge.recipient;
  return (
    <li className="py-2 border-b border-stone-100 last:border-0">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4">
        <div className="text-stone-900">
          <PartyLink party={party} individual={side === 'recipient' && edge.individual} />
        </div>
        <div className="font-medium tabular-nums">{formatAmount(edge.amount)}</div>
      </div>
      {edge.purpose !== null ? <div className="text-sm text-stone-700">{edge.purpose}</div> : null}
      <div className="mt-1 flex flex-wrap items-center gap-2">
        <ProvenanceNote provenance={edge.provenance} />
        {edge.program !== null ? <Badge>{edge.program}</Badge> : null}
        {side === 'recipient' && edge.basis === 'resolved' ? <Badge tone="teal">resolved: {TIER_LABEL[edge.tier ?? ''] ?? edge.tier}</Badge> : null}
        {edge.confirmation !== null ? (
          <Badge tone={edge.confirmation.amountAgrees ? 'teal' : 'amber'}>{edge.confirmation.amountAgrees ? 'confirmed by a later filing' : 'confirmed by a later filing; amounts differ'}</Badge>
        ) : null}
        {edge.part === 'web-grant' && edge.confirmation === null ? <Badge tone="amber">from the funder's website; not yet in a filing</Badge> : null}
      </div>
    </li>
  );
};

export const Pager = ({ page, pages, onPage }: { page: number; pages: number; onPage: (page: number) => void }) =>
  pages <= 1 ? null : (
    <div className="mt-3 flex items-center gap-3 text-sm">
      <button type="button" className="rounded border border-stone-300 px-2 py-1 disabled:opacity-40" disabled={page <= 1} onClick={() => onPage(page - 1)}>
        Previous
      </button>
      <span>
        Page {formatCount(page)} of {formatCount(pages)}
      </span>
      <button type="button" className="rounded border border-stone-300 px-2 py-1 disabled:opacity-40" disabled={page >= pages} onClick={() => onPage(page + 1)}>
        Next
      </button>
    </div>
  );
